import 'react-complex-tree/lib/style.css';
import '@/styles/globals.css';

import { supabase } from '../services/auth';
import type { AppProps } from 'next/app';
import Router, { useRouter } from 'next/router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import UserContext from '@/components/UserContext';
import Head from 'next/head';
import Script from 'next/script';
import { ThemeProvider } from '@/contexts/ThemeContext';

import { start } from '../services/bugsnag';
import { UserDetails } from '@/services/supabase-types';

start();

// Layout effect on the client so the session is read before the first paint after
// hydration; plain effect on the server, where neither runs.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** Keep the server-side auth cookie (used by API routes and SSR) in step with the browser session. */
function postAuthEvent(event: AuthChangeEvent | 'SIGNED_IN', session: Session | null) {
  return fetch('/api/auth', {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify({ event, session }),
  });
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  // Self-canonical for every page (query and hash stripped); pages override it with key="canonical".
  const canonicalPath = (router.asPath || '/').split(/[?#]/)[0];
  const canonicalUrl = `https://svg.new${canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '')}`;
  // Starts null so the hydrated markup matches the server render; the mount layout
  // effect below fills it from the stored session synchronously, without waiting for
  // the /api/auth round trip.
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  // The user id whose session the server cookie has been synced to. /api/user reads
  // that cookie, so details are only fetched once it matches.
  const [syncedUserId, setSyncedUserId] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  // The user id the last details fetch finished for (successfully or not).
  const [detailsUserId, setDetailsUserId] = useState<string | null>(null);
  const detailsRequest = useRef(0);

  const fetchUserDetails = useCallback(async () => {
    const sessionUserId = supabase.auth.session()?.user?.id ?? null;
    const id = ++detailsRequest.current;
    if (!sessionUserId) {
      setUserDetails(null);
      setDetailsUserId(null);
      return;
    }
    let details: UserDetails | null = null;
    try {
      const res = await fetch('/api/user', { method: 'GET', credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        details = data && typeof data === 'object' && !('error' in data) ? (data as UserDetails) : null;
      }
    } catch {
      details = null;
    }
    // A newer fetch or a sign-out superseded this one.
    if (id !== detailsRequest.current) return;
    setUserDetails(details);
    setDetailsUserId(sessionUserId);
  }, []);

  useIsomorphicLayoutEffect(() => {
    let active = true;
    const session = supabase.auth.session();
    setUser(session?.user ?? null);
    // An expired access token is refreshed asynchronously; until that settles the
    // session reads as empty, so don't report "signed out" yet (account would bounce
    // to /login). The first auth event, or a timeout, ends the wait.
    let storedSession = false;
    try {
      storedSession = !!window.localStorage.getItem('supabase.auth.token');
    } catch {
      storedSession = false;
    }
    const refreshPending = !session && storedSession;
    setAuthReady(!refreshPending);
    const readyTimer = refreshPending ? setTimeout(() => active && setAuthReady(true), 6000) : undefined;
    if (session?.user) {
      const userId = session.user.id;
      postAuthEvent('SIGNED_IN', session)
        .catch(() => {})
        .finally(() => {
          if (active) setSyncedUserId(userId);
        });
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthReady(true);
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        detailsRequest.current++;
        setUser(null);
        setUserDetails(null);
        setDetailsUserId(null);
        setSyncedUserId(null);
      } else if (session?.user) {
        setUser(session.user);
      }

      // A password-reset link signs the user in and then fires this; send them to
      // the new-password form wherever the link landed.
      if (event === 'PASSWORD_RECOVERY') {
        const onForm = Router.pathname === '/login' && Router.query.view === 'update_password';
        if (!onForm) Router.push('/login?view=update_password');
      }

      const userId = session?.user?.id ?? null;
      postAuthEvent(event, session)
        .catch(() => {})
        .finally(() => {
          if (active && userId && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
            setSyncedUserId(userId);
          }
        });
    });

    return () => {
      active = false;
      if (readyTimer) clearTimeout(readyTimer);
      authListener?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const umami = (window as any).umami;
    if (!umami?.identify) return;
    // umami rejects a non-object payload with 400; pass an object or nothing.
    if (user?.id) umami.identify({ userId: user.id });
  }, [user?.id]);

  // Load details once the server cookie belongs to the current user.
  useEffect(() => {
    if (user && syncedUserId === user.id) fetchUserDetails();
  }, [user?.id, syncedUserId, fetchUserDetails]);

  // _document's og/twitter title and description fallbacks describe the server-rendered page;
  // drop them once the router moves on so they don't go stale next to the new page's tags.
  useEffect(() => {
    const dropFallbacks = () =>
      document.querySelectorAll('meta[data-social-fallback]').forEach((el) => el.remove());
    Router.events.on('routeChangeComplete', dropFallbacks);
    return () => Router.events.off('routeChangeComplete', dropFallbacks);
  }, []);

  const userDetailsLoading = !!user && detailsUserId !== user.id;
  const authSynced = !!user && syncedUserId === user.id;

  return (
    <ThemeProvider>
      <UserContext.Provider
        value={{ user, supabase, userDetails, refreshUserDetails: fetchUserDetails, authReady, userDetailsLoading, authSynced }}
      >
        <Head>
          <title>SVG.new: Convert Images to SVG with AI</title>
          <meta name="description" content="Convert PNG, JPG, and WebP images to clean, scalable SVG vectors in seconds. Full-color auto-tracing with a built-in editor. Free to try, no sign-up required." />
          <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
          <meta name="keywords" content="image to SVG, vectorize image, PNG to SVG, JPG to SVG, raster to vector, image tracing, auto trace, vector converter, SVG editor, free vectorizer" />

          {/* Open Graph. og:title/og:description and twitter:title/twitter:description come from the
              page's own <title> and description (see _document) unless the page sets them itself. */}
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:url" property="og:url" content={canonicalUrl} />
          <link key="canonical" rel="canonical" href={canonicalUrl} />
          <meta key="og:image" property="og:image" content="https://svg.new/og.png" />
          <meta key="og:image:width" property="og:image:width" content="1200" />
          <meta key="og:image:height" property="og:image:height" content="630" />
          <meta key="og:site_name" property="og:site_name" content="SVG.new" />

          {/* Twitter Card */}
          <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
          <meta key="twitter:image" name="twitter:image" content="https://svg.new/og.png" />

          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
          <link rel="icon" href="/favicon.ico" />
        </Head>
        <Script
          defer
          src="https://what.vectorart.ai/script.js"
          data-website-id="ce12cb5d-affc-433f-b538-d76570e64a3c"
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&family=Inter:wght@100..900&family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
        {/* <div className="beautiful-background" /> */}
        <Component {...pageProps} />
      </UserContext.Provider>
    </ThemeProvider>
  );
}
